import React, { useEffect, useState, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FirmwareBundlesModalContent from "./FirmwareBundlesModalContent";
import { fetchOblacDrivesReleases, compareOblacDrivesReleasesByDateDesc } from "motion-master-client";
import { Modal } from "./Modal";
import { prerelease } from "semver";

/**
 * Opens modal automatically when URL matches.
 * Supports /latest or any specific release.
 */
const FirmwareModalManager = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const pathParts = location.pathname.split("/").filter(Boolean);
  const oblacDrivesVersionFromUrl = pathParts[0] || null;
  const firmwareVersionFromUrl = pathParts[1] || null;
  const activeTabFromUrl = pathParts[2] || "packages";

  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(false);
  const [resolvedOblacDrivesVersion, setResolvedOblacDrivesVersion] = useState(null);

  const fetchReleases = useCallback(async () => {
    setLoading(true);
    try {
      const [productionReleases] = await fetchOblacDrivesReleases(false);
      productionReleases.sort(compareOblacDrivesReleasesByDateDesc);
      setReleases(productionReleases);

      if (oblacDrivesVersionFromUrl === "latest") {
        const firstStableRelease = productionReleases?.filter((r) => r.supported && !prerelease(r.version))?.[0];
        setResolvedOblacDrivesVersion(firstStableRelease?.version || null);
      } else {
        setResolvedOblacDrivesVersion(oblacDrivesVersionFromUrl);
      }
    } catch (e) {
      console.error("Failed to fetch oblac drives releases", e);
    } finally {
      setLoading(false);
    }
  }, [oblacDrivesVersionFromUrl]);

  useEffect(() => {
    if (oblacDrivesVersionFromUrl) fetchReleases();
  }, [oblacDrivesVersionFromUrl, fetchReleases]);

  const isReleaseValid =
    resolvedOblacDrivesVersion && releases.some((r) => r.version === resolvedOblacDrivesVersion);

  const handleClose = () => navigate("/");

  if (!oblacDrivesVersionFromUrl) return null;

  const currentRelease = releases.find((r) => r.version === resolvedOblacDrivesVersion);

  return (
    <Modal
      title={`SOMANET Firmwares (${currentRelease?.firmwares?.length ?? 0}) Compatible with OBLAC Drives v${resolvedOblacDrivesVersion || "?"}`}
      isOpen={!!oblacDrivesVersionFromUrl}
      size="xl"
      isScrollable
      onClose={handleClose}
    >
      {loading ? (
        <div>Loading releases...</div>
      ) : !isReleaseValid ? (
        <div>
          OBLAC Drives release <strong>{oblacDrivesVersionFromUrl}</strong> does not exist.
        </div>
      ) : (
        <FirmwareBundlesModalContent
          oblacDrivesVersion={resolvedOblacDrivesVersion}
          firmwareVersion={
            firmwareVersionFromUrl?.startsWith("v")
              ? firmwareVersionFromUrl.slice(1)
              : firmwareVersionFromUrl
          }
          activeTab={activeTabFromUrl}
          releases={releases}
        />
      )}
    </Modal>
  );
};

export default FirmwareModalManager;
